// Utils
import { useAccountStore } from '@/store/modules/account/store.js';
import { escapeRegExp, get } from 'lodash-es';
import ClientCardSection from '@/entities/ClientCardSection.js';
import { parseCustomField } from '@/value-objects/CustomField.js';
import { isEnglishLocale } from '@/value-objects/dateEnvironment.ts';
import ActivixDate from '@/value-objects/ActivixDate.js';
import TaskEventType from '@/entities/TaskEventType.js';
import UserPhone from '@/entities/UserPhone.js';
import {
    getContentDisplayName,
    getRegionalDisplayName,
    localeForContentLanguage,
} from '@/utils/translations.ts';
import i18n from '@/plugins/vue-i18n.js';
import { formatPhone } from '@/utils/index.js';

// Entities
import CommunicationType from '../entities/CommunicationType.js';
import LeadVehicle from '../entities/LeadVehicle.ts';
import Service from '../entities/Service.js';

const canonicalContactTagValue = ({ attribute, customerProfileOutput, html, locale }) => {
    const contact = customerProfileOutput?.contact;

    if (!contact) {
        return null;
    }

    const emailByType = (type) =>
        contact.emails?.find((email) => email.type === type)?.address || '';
    const phoneByType = (type) =>
        contact.phones?.find((phone) => phone.type === type)?.display_number || '';
    let civility = contact.civility;

    if (!civility && contact.sex) {
        civility = contact.sex === 'M' ? 'mr' : 'ms';
    }
    const prefix = civility ? i18n.t(`civilities.${civility}`, {}, { locale }) : '';
    const values = {
        prefix: html ? `<span class="civility">${prefix}</span>` : prefix,
        gender: contact.sex === 'W' && locale.toLowerCase() === 'fr' ? 'e' : '',
        'lead-first-name': contact.first_name || '',
        'lead-last-name': contact.last_name || '',
        'lead-full-name': contact.full_name || '',
        'lead-email': contact.preferred_email?.address || '',
        'lead-work-email': emailByType('work'),
        'lead-home-email': emailByType('home'),
        'lead-phone': contact.preferred_phone?.display_number || '',
        'lead-cell-phone': phoneByType('cell'),
        'lead-home-phone': phoneByType('home'),
        'lead-work-phone': phoneByType('work'),
        'lead-business-name': contact.business_name || '',
        'lead-address': contact.address?.street || '',
        'lead-city': contact.address?.city || '',
        'lead-province': contact.address?.province || '',
        'lead-country': contact.address?.country || '',
        'lead-postal-code': contact.address?.postal_code || '',
        'lead-second-contact': '',
    };

    return Object.hasOwn(values, attribute.tag) ? values[attribute.tag] : null;
};

const getLeadValue = ({ attribute, html, lead, locale, prefix }) => {
    switch (attribute.tag) {
        case 'prefix':
            return getPrefix({ prefix, lead, html });

        case 'gender':
            return locale.toLowerCase() == 'fr' && lead.sex == 'W' ? 'e' : '';

        case 'response-subject':
            return getResponseSubject(lead);

        case 'dealer-website':
            return getDealerWebsite({ lead, html });

        case 'wanted-vehicle-link':
            return getWantedVehicleLink({ lead, html, locale });

        case 'wanted-vehicle-drs-link-dealer':
            return getWantedVehicleDrsLinkDealer({ lead, html, locale });

        case 'wanted-vehicle-drs-link-client':
            return getWantedVehicleDrsLinkClient({ lead, html, locale });

        case 'wanted-stock-state':
            return getStockState({ lead, locale });

        case 'exchange-equity':
            if (lead.exchange_vehicles.length) {
                return lead.exchange_vehicles[0].calculated_equity;
            }

            break;

        case 'exchange-month-remaining':
            if (lead.exchange_vehicles.length) {
                return LeadVehicle.calculateMonthsRemaining(
                    lead.exchange_vehicles[0].end_contract_date,
                    true
                );
            }

            break;

        case 'commercial-institution':
            return getInstitution(lead);

        case 'wanted-vehicle-drs-trade-in-link':
            return getTradeInLink({ lead, locale });

        case 'wanted-vehicle-drs-deposit-link':
            return getDepositLink({ lead, locale });

        case 'wanted-vehicle-drs-credit-application-link':
            return getCreditApplicationLink({ lead, locale });

        case 'wanted-vehicle-drs-appointment-link':
            return getAppointmentLink({ lead, locale });

        case 'subscribe':
            return getSubscribe(locale);

        case 'lead-division':
            return getDivisionName({ lead, locale });

        case 'lead-form':
            return getFormName({ lead, locale });

        case 'lead-approved':
            return formatBoolean(lead.approved, locale);

        case 'business-hours-new':
        case 'business-hours-used':
        case 'business-hours-service':
            return attribute[`value_${locale}`];
    }

    return '';
};

const getAdvisorValue = ({ attribute, lead, locale }) => {
    switch (attribute.tag) {
        // @TODO Rename "firstName" to "first-name"
        case 'assigned-advisor-firstName':
            return lead.user?.first_name;

        case 'assigned-advisor-lastName':
            return lead.user?.last_name;

        case 'assigned-advisor-name':
            return lead.user?.fullName;

        case 'assigned-advisor-post': {
            return getUserPost({
                post: lead.user?.post,
                locale,
            });
        }

        case 'assigned-advisor-phone': {
            const phones = (lead.user?.phones || []).map((phone) => new UserPhone(phone));

            return getUserPhone(phones);
        }

        case 'assigned-advisor-email':
            return lead.user.email || '';
    }

    return '';
};

const getBDCValue = ({ attribute, lead, locale }) => {
    switch (attribute.tag) {
        // @TODO Rename "firstName" to "first-name"
        case 'assigned-bdc-first-name':
            return lead.bdcUser.first_name;

        case 'assigned-bdc-name':
            return lead.bdcUser.fullName;

        case 'assigned-bdc-post': {
            const user = lead.bdcUser;

            return getUserPost({
                post: user?.post,
                locale,
            });
        }

        case 'assigned-bdc-phone': {
            const user = lead.bdcUser;
            const phones = (user?.phones || []).map((phone) => new UserPhone(phone));

            return getUserPhone(phones);
        }

        case 'assigned-bdc-email':
            return lead.bdcUser.email || '';
    }

    return '';
};

const getCommercialValue = ({ attribute, lead }) => {
    switch (attribute.tag) {
        case 'commercial-assigned-director-email':
            return lead.commercialUser.email || '';

        default:
            return lead.commercialUser.fullName;
    }
};

const getDeliveryValue = ({ attribute, lead, locale }) => {
    switch (attribute.tag) {
        case 'assigned-delivery-user-first-name':
            return lead.deliveredByUser.first_name;

        case 'assigned-delivery-user-name':
            return lead.deliveredByUser.fullName;

        case 'assigned-delivery-user-post': {
            const user = lead.deliveredByUser;

            return getUserPost({
                post: user?.post,
                locale,
            });
        }

        case 'assigned-delivery-user-phone': {
            const user = lead.deliveredByUser;
            const phones = (user?.phones || []).map((phone) => new UserPhone(phone));

            return getUserPhone(phones);
        }

        case 'assigned-delivery-user-email':
            return lead.deliveredByUser.email || '';
    }

    return '';
};

const getCustomableValue = ({ attribute, lead }) => {
    let customField = lead.account.getCustomFieldByField(attribute.tag);

    if (!customField) {
        customField = useAccountStore().contextAccount.getCustomFieldByField(attribute.tag);
    }

    if (!customField) {
        return '';
    }

    let customable;

    if (ClientCardSection.isVehicleField(customField.section)) {
        customable = lead.getCustomableVehicleById(customField.id, customField.section);
    } else {
        customable = lead.getCustomableById(customField.id);
    }

    if (!customable) {
        if (customField.type === 'boolean') {
            return i18n.t('general.no');
        }

        return '';
    }

    return parseCustomField.toRead(customable.pivot.value, customField.type);
};

const getMassMailingValue = ({ attribute, groupName }) => {
    return normalizeTagText(`{${groupName} - ${attribute.text}}`);
};

const getVideoCommunicationThumbnail = ({ locale }) => {
    return `
        <thumbnail data-video-communication-thumbnail class='communication-video-placeholder-wrapper' style="background: rgba(0, 0, 0, .35) url('/img/cars.jpeg')">
            <div class="communication-video-placeholder" style="backdrop-filter: blur(3px);">{ ${i18n.t(
                'communications.video.thumbnailPlaceholder',
                {},
                { locale }
            )} }</div>
        </thumbnail>
    `;
};

const getDateAttributeFormat = (formatJs, locale) => {
    if (formatJs === 'time') {
        return isEnglishLocale(locale) ? 'h:mm a' : 'HH:mm';
    }

    if (formatJs === 'date') {
        return isEnglishLocale(locale) ? 'MMMM do, yyyy' : 'dd MMMM yyyy';
    }

    if (isEnglishLocale(locale)) {
        return "MMMM do, yyyy 'at' h:mm a";
    }

    return "dd MMMM yyyy 'à' HH:mm";
};

const getDateAttributeType = (formatJs, value) => {
    if (formatJs === 'date' && typeof value === 'string' && value.length > 10) {
        return 'dateTime';
    }

    return formatJs;
};

const getClosestAppointmentThreshold = () => {
    // Replicates Niotext closestAppointment cutoff.
    return new ActivixDate(new Date().toISOString()).startOfDay().addMinutes(6 * 60);
};

const getClosestAppointmentTaskEvent = (lead) => {
    const taskEvents = lead?.task_events || lead?.taskEvents || [];
    const threshold = getClosestAppointmentThreshold();
    const appointmentTypeIds = new Set([
        TaskEventType.APPOINTMENT,
        TaskEventType.PHONE_APPOINTMENT,
        TaskEventType.DELIVERY,
    ]);

    return taskEvents
        .filter((taskEvent) => appointmentTypeIds.has(taskEvent.task_event_type_id))
        .filter((taskEvent) => new ActivixDate(taskEvent.start_at).isAfter(threshold.toDate()))
        .sort((first, second) => {
            return (
                new ActivixDate(first.start_at).timestamp -
                new ActivixDate(second.start_at).timestamp
            );
        })[0];
};

const getDynamicModelValue = (payload, model) => {
    const dynamicVariable = get(payload, model);

    if (dynamicVariable || !model.startsWith('taskEvent.')) {
        return dynamicVariable;
    }

    const taskEvent = getClosestAppointmentTaskEvent(payload.lead);

    return taskEvent ? get({ taskEvent }, model) : null;
};

const getModelValue = (payload) => {
    const { attribute, locale } = payload;
    let value = '';

    attribute.model.forEach((model) => {
        value += !value ? '' : ' ';

        const dynamicVariable = getDynamicModelValue(payload, model);

        if (!dynamicVariable) {
            return;
        }

        if (attribute.formatJs) {
            value += new ActivixDate(
                dynamicVariable,
                getDateAttributeType(attribute.formatJs, dynamicVariable)
            ).format(
                getDateAttributeFormat(attribute.formatJs, locale),
                ActivixDate.timezone,
                locale
            );
            return;
        }

        value += dynamicVariable;
    });

    return value;
};

const getPrefix = ({ prefix, lead, html }) => {
    const civility = prefix || (lead.civility ? i18n.t(`civilities.${lead.civility}`) : '');

    if (html) {
        return `<span class="civility">${civility}</span>`;
    }

    return civility;
};

const getResponseSubject = (lead) => {
    const communication = lead.communications.find((communication) => {
        return (
            communication.communication_type_id == CommunicationType.INCOMING &&
            communication.service_id == Service.LEAD_XPRESS &&
            !!communication.email_subject
        );
    });

    if (!communication) {
        return '';
    }

    return `Re: ${communication.email_subject}`;
};

const getSenderValue = ({ attribute, locale, sender }) => {
    switch (attribute.tag) {
        case 'sender-advisor-first-name':
            return sender.first_name;

        case 'sender-advisor-last-name':
            return sender.last_name;

        case 'sender-advisor-full-name':
            return sender.fullName;

        case 'sender-advisor-phone':
            return getUserPhone(sender.phones);

        case 'sender-advisor-post':
            return getUserPost({
                post: sender.post,
                locale,
            });
    }

    return '';
};

const getUserPhone = (phones) => {
    const preferredPhone = phones.find((p) => p.preferred) || phones[0];

    if (!preferredPhone) {
        return '';
    }

    const number = formatPhone(preferredPhone.phone);
    const extension = preferredPhone.extension ? `#${preferredPhone.extension}` : '';

    return `${number} ${extension}`.trim();
};

const getUserPost = ({ post, locale }) => {
    if (!post) {
        return '';
    }

    return i18n.t(`database.post_${post.name}`, {}, { locale });
};

const getDivisionName = ({ lead, locale }) => {
    if (!lead || !lead.division) {
        return '';
    }

    return i18n.t(`divisions.${lead.division.name}`, {}, { locale });
};

const getFormName = ({ lead, locale }) => {
    if (!lead) {
        return '';
    }

    if (lead.lead_form) {
        if (lead.lead_form.account_id) {
            return getContentDisplayName(lead.lead_form, locale);
        }

        const regionalLocale = localeForContentLanguage(locale, lead.account?.country);

        return regionalLocale
            ? getRegionalDisplayName(lead.lead_form, regionalLocale)
            : lead.lead_form.name;
    }

    return lead.form || '';
};

const getStockState = ({ lead, locale }) => {
    if (
        !lead ||
        !lead.getSoldOrFirstWantedVehicle() ||
        !lead.getSoldOrFirstWantedVehicle().stock_state
    ) {
        return '';
    }

    return i18n.t(
        `leadVehicleStockStates.${lead.getSoldOrFirstWantedVehicle().stock_state}`,
        {},
        { locale }
    );
};

const formatBoolean = (value, locale) => {
    return value ? i18n.t('general.yes', {}, { locale }) : i18n.t('general.no', {}, { locale });
};

const getWantedVehicleLink = ({ lead, html, locale }) => {
    const vehicle = lead.getSoldOrFirstWantedVehicle();

    if (!vehicle || !vehicle.url) {
        return '';
    }

    if (html) {
        return `
            <a href="${vehicle.url}">
                ${i18n.t('responseLeadXpress.wantedVehicleLink', {}, { locale })}
            </a>
        `;
    }

    return vehicle.url;
};
const getDealerWebsite = ({ lead, html }) => {
    if (!lead.account || !lead.account.website) {
        return '';
    }

    if (html) {
        return `
            <a href="${lead.account.website}">
                ${lead.account.website}
            </a>
        `;
    }

    return lead.account.website;
};

const getWantedVehicleDrsLinkDealer = ({ lead, html, locale }) => {
    const vehicle = lead.getSoldOrFirstWantedVehicle();

    if (!vehicle || !vehicle.drs_link_dealer) {
        return '';
    }

    if (html) {
        return `
            <a href="${vehicle.drs_link_dealer}">
                ${i18n.t('responseLeadXpress.wantedVehicleDrsLinkDealer', {}, { locale })}
            </a>
        `;
    }

    return vehicle.drs_link_dealer;
};

const getWantedVehicleDrsLinkClient = ({ lead, html, locale }) => {
    const vehicle = lead.getSoldOrFirstWantedVehicle();

    if (!vehicle || !vehicle.drs_link_client) {
        return '';
    }

    if (html) {
        return `
            <a href="${vehicle.drs_link_client}">
                ${i18n.t('responseLeadXpress.wantedVehicleDrsLinkClient', {}, { locale })}
            </a>
        `;
    }

    return vehicle.drs_link_client;
};

const getInstitution = (lead) => {
    if (lead.lead_bank) {
        return lead.lead_bank.name;
    }

    if (lead.imported_bank) {
        return lead.imported_bank;
    }

    return '';
};

const getTradeInLink = ({ lead, locale }) => {
    const vehicle = lead.getSoldOrFirstWantedVehicle();

    if (!vehicle?.drs_trade_in_link) {
        return '';
    }

    return `
        <a rel="noopener" href="${vehicle.drs_trade_in_link}" target="_blank">
            ${i18n.t('responseLeadXpress.easyLinks.tradeIn', {}, { locale })}
        </a>
    `;
};

const getDepositLink = ({ lead, locale }) => {
    const vehicle = lead.getSoldOrFirstWantedVehicle();

    if (!vehicle?.drs_deposit_link) {
        return '';
    }

    return `
        <a rel="noopener" href="${vehicle.drs_deposit_link}" target="_blank">
            ${i18n.t('responseLeadXpress.easyLinks.deposit', {}, { locale })}
        </a>
    `;
};

const getCreditApplicationLink = ({ lead, locale }) => {
    const vehicle = lead.getSoldOrFirstWantedVehicle();

    if (!vehicle?.drs_credit_application_link) {
        return '';
    }

    return `
        <a rel="noopener" href="${vehicle.drs_credit_application_link}" target="_blank">
            ${i18n.t('responseLeadXpress.easyLinks.creditApplication', {}, { locale })}
        </a>
    `;
};

const getAppointmentLink = ({ lead, locale }) => {
    const vehicle = lead.getSoldOrFirstWantedVehicle();

    if (!vehicle?.drs_appointment_link) {
        return '';
    }

    return `
        <a rel="noopener" href="${vehicle.drs_appointment_link}" target="_blank">
            ${i18n.t('responseLeadXpress.easyLinks.appointment', {}, { locale })}
        </a>
    `;
};

const getSubscribe = (locale) => {
    return `
            <a rel="noopener" href="%tag_subscribe_url%" target="_blank">
                ${i18n.t('templates.subscribe', {}, { locale })}
            </a>
        `;
};

const normalizeTagText = (content) => {
    if (content.includes('&amp;')) {
        return content.replace(/&amp;/g, '&');
    }

    return content;
};

const unresolvedAttribute = Symbol('unresolvedAttribute');

const getDirectLegacyAttributeValue = (payload) => {
    const { attribute, locale } = payload;
    let value = unresolvedAttribute;

    switch (attribute.tag) {
        case 'date':
            value = new ActivixDate(new Date().toISOString(), 'date').format(
                getDateAttributeFormat('date', locale),
                ActivixDate.timezone,
                locale
            );
            break;
        case 'dealer-phone':
            value = formatPhone(get(payload, 'lead.account.phone'));
            break;
        case 'lead-phone':
            value = formatPhone(get(payload, 'lead.lead_phones[0].phone'));
            break;
        case 'lead-cell-phone':
            value = formatPhone(payload.lead?.cellPhones[0]?.phone);
            break;
        case 'lead-home-phone':
            value = formatPhone(payload.lead?.homePhones[0]?.phone);
            break;
        case 'lead-work-phone':
            value = formatPhone(payload.lead?.workPhones[0]?.phone);
            break;
        case 'lead-home-email':
            value = payload.lead?.homeEmails[0]?.email;
            break;
        case 'lead-work-email':
            value = payload.lead?.workEmails[0]?.email;
            break;
        default:
            break;
    }

    return value;
};

const getAssignedUserAttributeValue = (payload) => {
    const { tag } = payload.attribute;
    let value = unresolvedAttribute;

    if (tag.startsWith('assigned-advisor-')) {
        value = getAdvisorValue(payload);
    } else if (tag.startsWith('assigned-bdc-')) {
        value = getBDCValue(payload);
    } else if (tag.startsWith('commercial-assigned-')) {
        value = getCommercialValue(payload);
    } else if (tag.startsWith('assigned-delivery-user-')) {
        value = getDeliveryValue(payload);
    }

    return value;
};

const getLegacyAttributeValue = (payload) => {
    const { attribute } = payload;
    let value = getDirectLegacyAttributeValue(payload);

    if (value === unresolvedAttribute) {
        value = getAssignedUserAttributeValue(payload);
    }

    if (value === unresolvedAttribute && attribute.tag === 'video-communication-thumbnail') {
        value = getVideoCommunicationThumbnail(payload);
    }

    if (value === unresolvedAttribute && attribute.tag.startsWith('custom_')) {
        value = getCustomableValue(payload);
    }

    if (value === unresolvedAttribute && Array.isArray(attribute.model) && attribute.model.length) {
        value = getModelValue(payload);
    }

    return value === unresolvedAttribute ? getLeadValue(payload) : value;
};

export default {
    computed: {
        templateAttributes() {
            return useAccountStore().templateAttributes;
        },
    },

    methods: {
        normalizeTagText(content) {
            return normalizeTagText(content);
        },

        replaceAttributesInMessage(payload) {
            let { message, html } = payload;

            const attributesToReplace = this.templateAttributes.reduce((attributes, group) => {
                const groupAttributes = group.tags.reduce((groupAttributes, attribute) => {
                    const htmlTag = `<span class="${attribute.tag}"></span>`;
                    let attributeTagFr = `{${group.name_fr} - ${attribute.text_fr}}`;
                    let attributeTagEn = `{${group.name_en} - ${attribute.text_en}}`;

                    attributeTagFr = html ? attributeTagFr : this.normalizeTagText(attributeTagFr);
                    attributeTagEn = html ? attributeTagEn : this.normalizeTagText(attributeTagEn);

                    const messageIncludesAttribute = [htmlTag, attributeTagFr, attributeTagEn].some(
                        (tag) => message.includes(tag)
                    );

                    if (messageIncludesAttribute) {
                        groupAttributes[attribute.tag] = {
                            attribute,
                            groupName: group.name,
                            tags: [
                                htmlTag,
                                escapeRegExp(attributeTagFr),
                                escapeRegExp(attributeTagEn),
                            ],
                        };
                    }

                    return groupAttributes;
                }, {});

                return {
                    ...attributes,
                    ...groupAttributes,
                };
            }, {});

            const values = Object.values(attributesToReplace).map(
                ({ attribute, groupName, tags }) => {
                    const value = this.getAttributeValue({
                        groupName,
                        attribute,
                        ...payload,
                    });

                    return { value, tags };
                }
            );

            values.forEach(({ value, tags }) => {
                for (const tag of tags) {
                    message = message.replace(new RegExp(tag, 'g'), value);
                }
            });

            return message;
        },

        getAttributeValue(payload) {
            const { attribute, sender, massMailing, notification } = payload;

            if (attribute.tag.startsWith('sender-')) {
                if (!sender) {
                    return getMassMailingValue(payload);
                }

                return getSenderValue(payload);
            }

            if (massMailing || notification) {
                return getMassMailingValue(payload);
            }

            const canonicalValue = canonicalContactTagValue(payload);

            if (canonicalValue !== null) {
                return canonicalValue;
            }

            return getLegacyAttributeValue(payload);
        },
    },
};
